import Tooltip from '@material-ui/core/Tooltip';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import GetAppIcon from '@material-ui/icons/GetApp';
import { truncate } from 'lodash';
import React from 'react';
import { DateTime } from '~domain/model/core/date/datetime';
import { policyAttachmentTypeConfig } from '~domain/model/policy/attachment/policy-attachment-config';
import { PolicyAttachmentType } from '~domain/model/policy/attachment/policy-attachment-type';
import { PolicyAttachmentSummary } from '~domain/model/policy/policy-attachment-summary';
import { useTranslator } from '~frontend-common/i18n/translator';
import { Box } from '~ui-kit/components/box';
import { Button, ButtonProps } from '~ui-kit/components/button/button';
import { Typography } from '~ui-kit/components/typography';
import { theme } from '~ui-kit/theme';
import { Img } from '../ui';
import { getPolicyAttachmentCategoryIcon } from './policy-attachment-categories';

interface PolicyAttachmentCardProps {
  attachment: PolicyAttachmentSummary;
  showNotificationIcon?: boolean;
}

export function PolicyAttachmentCard({ attachment, showNotificationIcon = false }: PolicyAttachmentCardProps) {
  const translator = useTranslator();
  const { t, getCurrentLanguageId } = translator;
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints[0]})`);

  const date = new DateTime(attachment.receivedDate).Moment.locale(getCurrentLanguageId()).format('LL');
  const iconSrc = getPolicyAttachmentCategoryIcon(attachment.type);
  const attachmentConfig = policyAttachmentTypeConfig[attachment.type];
  const isPrivate = !attachmentConfig?.isPublic;

  let notifiableTooltip: string;
  if (attachmentConfig?.notifyUser) {
    notifiableTooltip = t('policies.documents.notifyUserLabel');
  } else if (attachmentConfig?.notifyBroker) {
    notifiableTooltip = t('policies.documents.notifyBrokerLabel');
  }

  const downloadButtonProps: ButtonProps = isMobile
    ? {
        shape: 'round',
        appearance: 'none',
        size: 'lg',
        children: <GetAppIcon />,
      }
    : {
        appearance: 'outlined',
        size: 'sm',
        children: t('policies.documents.downloadButton'),
      };

  return (
    <Box flexDirection="row" alignItems="center" data-id={`attachment-${attachment.id}`}>
      <Box flexDirection="column" pl={2} pr={3}>
        <Img src={iconSrc} height={32} width={32} />
      </Box>

      <Box flexDirection="column" flex="1 1 auto">
        <Typography $fontSize="14px" $fontWeight={500}>
          {t(`policies.documents.types.${attachment.type}`)}{' '}
          {attachment.type === PolicyAttachmentType.Unknown && (
            <>
              <Typography $fontSize="12px" $fontWeight={400}>
                <Tooltip title={attachment.name}>
                  <span>({truncate(attachment.name, { length: 20 })})</span>
                </Tooltip>
              </Typography>{' '}
            </>
          )}
          {isPrivate && (
            <Tooltip title={t('policies.documents.privateLabel')}>
              <span>🔒</span>
            </Tooltip>
          )}
          {showNotificationIcon && notifiableTooltip && (
            <Tooltip title={notifiableTooltip}>
              <span>🔔</span>
            </Tooltip>
          )}
        </Typography>

        <Typography $fontSize="12px" $color={theme.colors.secondaryDark}>
          {date}
        </Typography>
      </Box>

      <Box flexDirection="column" justifyContent="center">
        <Button
          data-id="btn-download-attachment"
          id={`btn-download-attachment-${attachment.id}`}
          color="secondary"
          href={attachment.downloadUrl}
          target="_blank"
          {...downloadButtonProps}
        />
      </Box>
    </Box>
  );
}
